import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, ErrorState, Input } from '@helios/ui';
import { platformPurgeClient, purgeRevocationIssues, type PurgePreview, type PurgeResult, type PurgeScope } from '@/services/platformPurgeClient';
import { useAuth } from '@/features/auth/authContext';
import { SettingsPageFrame } from './settingsChrome';
import { Field, selectClassName } from './settingsShared';

/** Super-admin danger zone: dry run first, then typed phrase + password. */
export function PlatformPurgePage() {
  const auth = useAuth();
  const principal = `${auth.tenant?.id ?? ''}:${auth.user?.id ?? ''}:${auth.sessionId ?? ''}`;
  const status = useQuery({ queryKey: ['platform-purge', 'status', principal], gcTime: 0, placeholderData: undefined, queryFn: () => platformPurgeClient.status(), retry: false });
  const [scope, setScope] = useState<PurgeScope>('TENANT');
  const [tenantId, setTenantId] = useState('');
  const [keepUsers, setKeepUsers] = useState(true);
  const [preview, setPreview] = useState<PurgePreview | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [result, setResult] = useState<PurgeResult | null>(null);

  const dryRun = useMutation({
    mutationFn: () => platformPurgeClient.preview({ scope, tenantId: scope === 'TENANT' ? tenantId || status.data?.actorTenantId : undefined, keepUsers }),
    onSuccess: (data) => { setPreview(data); setConfirmation(''); setPassword(''); setResult(null); },
  });
  const run = useMutation({
    mutationFn: () => platformPurgeClient.execute({ previewId: preview!.previewId, confirmation, password }),
    onSuccess: (data) => { setResult(data); setPreview(null); setPassword(''); void status.refetch(); },
  });

  const data = status.data;
  return (
    <SettingsPageFrame title="Danger zone: purge data" description="Super admin only. Permanently deletes business data. Always run a dry run first; the purge itself needs the exact confirmation phrase and your password.">
      {status.isPending ? <p role="status" className="text-sm text-muted-foreground">Loading purge status…</p> : null}
      {status.isError ? <ErrorState title="Purge unavailable" description={status.error instanceof Error ? status.error.message : 'Status could not be loaded.'} /> : null}
      {data && !data.enabled ? <p role="status" className="text-sm text-warning">Disabled on this deployment. Set <code>PLATFORM_PURGE_ENABLED=true</code> and add <code>{data.principal}</code> to <code>PLATFORM_SUPER_ADMINS</code> on the API to enable it.</p> : null}
      {data && data.enabled && !data.eligible ? <p role="status" className="text-sm text-critical">Your account is not a platform super admin. To allow it, add <code>{data.principal}</code> to <code>PLATFORM_SUPER_ADMINS</code>.</p> : null}
      {data?.eligible ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <section aria-label="Dry run" className="border-critical/40 space-y-2 rounded-md border p-3">
            <Field label="Scope">
              <select className={selectClassName} value={scope} onChange={(event) => { setScope(event.target.value as PurgeScope); setPreview(null); }}>
                <option value="TENANT">Purge one tenant&apos;s business data</option>
                <option value="PLATFORM">Full platform reset (all tenants)</option>
              </select>
            </Field>
            {scope === 'TENANT' ? (
              <Field label="Tenant">
                <select className={selectClassName} value={tenantId || data.actorTenantId} onChange={(event) => { setTenantId(event.target.value); setPreview(null); }}>
                  {data.tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                </select>
              </Field>
            ) : null}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={keepUsers} onChange={(event) => { setKeepUsers(event.target.checked); setPreview(null); }} />
              Keep users, roles and sign-ins (your own account is always kept)
            </label>
            <Button size="sm" variant="outline" disabled={dryRun.isPending} onClick={() => dryRun.mutate()}>{dryRun.isPending ? 'Counting…' : 'Run dry run'}</Button>
            {dryRun.isError ? <p className="text-xs text-critical">{(dryRun.error as Error).message}</p> : null}
          </section>
          <section aria-label="Purge" className="space-y-2 rounded-md border p-3">
            {preview ? (
              <>
                <p className="text-sm font-semibold">{preview.totalRows.toLocaleString()} rows would be deleted</p>
                <p className="text-2xs text-muted-foreground">Revokes {preview.externalRevocations.plaidItems} Plaid item(s), {preview.externalRevocations.accountingConnections} QuickBooks, {preview.externalRevocations.procoreConnections} Procore, {preview.externalRevocations.metaConnections} Meta and {preview.externalRevocations.m365Connections} Microsoft 365 connection(s). Object storage: {preview.objectStorage.configured ? `${preview.objectStorage.referencedObjects} referenced files plus tenant prefixes` : 'not configured'}.</p>
                <div className="max-h-40 overflow-auto text-2xs" data-testid="purge-counts">
                  {preview.tenants.map((tenant) => (
                    <details key={tenant.id}>
                      <summary>{tenant.name}{tenant.dropped ? ' (tenant removed)' : ''}: {tenant.total.toLocaleString()}</summary>
                      <ul className="pl-3">{Object.entries(tenant.counts).sort((a, b) => b[1] - a[1]).map(([table, n]) => <li key={table}>{table}: {n.toLocaleString()}</li>)}</ul>
                    </details>
                  ))}
                </div>
                <p className="text-2xs text-muted-foreground">Kept: {preview.preserved.join('; ')}.</p>
                <Field label={`Type: ${preview.confirmationPhrase}`}><Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" /></Field>
                <Field label="Your password"><Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" /></Field>
                <Button size="sm" variant="destructive" disabled={run.isPending || confirmation !== preview.confirmationPhrase || !password} onClick={() => run.mutate()}>{run.isPending ? 'Purging…' : 'Permanently purge'}</Button>
                {run.isError ? <p className="text-xs text-critical">{(run.error as Error).message}</p> : null}
              </>
            ) : result ? (
              <div role="status" className="space-y-2">
                <p className="text-sm">{purgeRevocationIssues(result).length ? 'Database purge finished' : 'Purge complete'}: {result.totalRows.toLocaleString()} rows deleted, {result.storage.deleted} files removed{result.storage.failed ? `, ${result.storage.failed} file deletions failed` : ''}. Audit entry {result.auditId}.</p>
                {purgeRevocationIssues(result).length ? (
                  <div className="border-critical/40 rounded-md border p-2">
                    <p className="text-xs text-critical">Some third-party grants may still be live. Local credentials were already deleted, so revoke these manually now:</p>
                    <ul className="text-2xs text-critical mt-1 list-disc pl-4">
                      {purgeRevocationIssues(result).map((issue) => <li key={issue}>{issue}</li>)}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Run a dry run to see exactly what would be deleted.</p>
            )}
            {data.history.length ? (
              <ul className="text-2xs text-muted-foreground border-t pt-2">{data.history.map((entry) => <li key={entry.id}>{new Date(entry.createdAt).toLocaleString()} · {entry.scope} · {entry.targetTenantName} · {entry.outcome}{entry.storageOutcome && entry.storageOutcome !== 'COMPLETED' ? ` (files: ${entry.storageOutcome})` : ''} · {entry.actorEmail}</li>)}</ul>
            ) : null}
          </section>
        </div>
      ) : null}
    </SettingsPageFrame>
  );
}
